import { Children, isValidElement, type ForwardedRef, type ReactNode, type SelectHTMLAttributes } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { peerFocusRing } from '../lib/focus.js';
import { ICON } from '../lib/iconSize.js';

/* ===========================================================================
 * `<Select look="row">` — the value side of a settings-style row.
 *
 * The phone's bottom sheets draw a choice as a row: the field's name at the
 * start, the chosen value in primary ink and a chevron at the end, no box
 * (the owner's "Request leave" sheet). The same row look `DateTrigger` has, so
 * a sheet's rows are one language whichever control sits in them.
 *
 * STILL THE NATIVE SELECT. It covers the whole row, invisible, so a tap
 * anywhere opens the platform's own picker — the best one a phone has — and
 * everything a screen reader hears, `required`, `aria-invalid` and the label's
 * `htmlFor` are the real control's. What shows is the chosen option's own text,
 * read off the children, so the row can never name a different value from the
 * one the select holds.
 * ======================================================================== */

/** The text of the `<option>` whose value is `value`, searched through fragments and arrays. */
function optionText(children: ReactNode, value: unknown): string {
  let found = '';
  Children.forEach(children, (child) => {
    if (found || !isValidElement<{ value?: unknown; children?: ReactNode }>(child)) return;
    if (child.type === 'option' && String(child.props.value ?? '') === String(value ?? '')) {
      found = Children.toArray(child.props.children).join('');
    } else if (child.props.children !== undefined && child.type !== 'option') {
      found = optionText(child.props.children, value);
    }
  });
  return found;
}

export function SelectRowLook({
  selectRef,
  className,
  invalid,
  disabled,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & {
  selectRef: ForwardedRef<HTMLSelectElement>;
  invalid: boolean;
}): JSX.Element {
  const text = optionText(children, props.value);
  // The value-empty option is a prompt ("Select…"), so it reads as one.
  const empty = String(props.value ?? '') === '';
  return (
    <span className={cn('relative flex min-h-touch w-full min-w-0 items-center justify-end gap-tight', className)}>
      <select
        ref={selectRef}
        disabled={disabled}
        className="peer absolute inset-0 z-10 h-full w-full cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
        {...props}
        aria-invalid={invalid ? true : props['aria-invalid']}
        aria-required={props.required ? true : props['aria-required']}
      >
        {children}
      </select>
      <span aria-hidden="true" className={cn('pointer-events-none absolute inset-0 rounded-control', peerFocusRing)} />
      <span
        aria-hidden="true"
        className={cn(
          'truncate text-end text-copy-lg font-semibold peer-disabled:opacity-50',
          invalid ? 'text-danger' : empty ? 'text-muted-foreground' : 'text-primary'
        )}
      >
        {text}
      </span>
      <ChevronRight
        size={ICON.control}
        aria-hidden="true"
        className={cn('flex-none peer-disabled:opacity-50 rtl:-scale-x-100', invalid ? 'text-danger' : empty ? 'text-muted-foreground' : 'text-primary')}
      />
    </span>
  );
}
